import type { PresenceMinute } from "../types.js";

export const PRESENCE_MINUTE = 60000;
export type ActivityResolution = "minute" | "hour" | "day";
export const ACTIVITY_STEP = {
  minute: PRESENCE_MINUTE,
  hour: 60 * PRESENCE_MINUTE,
  day: 1440 * PRESENCE_MINUTE,
};

export interface ActivityBucket {
  time: number;
  counts: number[];
  values: number[] | null;
  observed: number;
  reported: number;
}

/** Add table-minute observations first, then divide once; never average per-minute percentages. */
export function aggregatePresenceActivity(
  rows: PresenceMinute[],
  resolution: ActivityResolution,
): ActivityBucket[] {
  const step = ACTIVITY_STEP[resolution];
  const buckets = new Map<number, ActivityBucket>();
  for (const row of rows) {
    const time = Math.floor(Date.parse(row.bucket) / step) * step;
    let bucket = buckets.get(time);
    if (!bucket) {
      bucket = { time, counts: [0, 0, 0, 0, 0], values: null, observed: 0, reported: 0 };
      buckets.set(time, bucket);
    }
    bucket.reported += row.reportingTables;
    if (row.successfulTables === 0 || row.activityCounts === null) continue;
    bucket.observed += row.successfulTables;
    row.activityCounts.forEach((count, index) => {
      bucket.counts[index]! += count;
    });
  }
  return [...buckets.values()]
    .sort((a, b) => a.time - b.time)
    .map((bucket) => ({
      ...bucket,
      values: bucket.observed
        ? bucket.counts.map((count) => (count / bucket.observed) * 100)
        : null,
    }));
}

export function possibleTableMinutes(
  time: number,
  step: number,
  range: { from: string; to: string },
  tables: number,
): number {
  const from = Math.max(time, Date.parse(range.from));
  const to = Math.min(time + step - 1, Date.parse(range.to));
  return to < from
    ? 0
    : (Math.floor(to / PRESENCE_MINUTE) - Math.floor(from / PRESENCE_MINUTE) + 1) * tables;
}
